import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { formatTime } from "../lib/format";
import { toast } from "./toast";
import { backdropClose } from "../lib/backdrop";

/**
 * "No issues" on every file that came through review clean.
 *
 * A Pickups Package only speaks about the files that had something wrong, so a
 * file with nothing in it reads the same as a file nobody checked. One flag
 * near the end of each clean file says "checked — nothing to fix", in the
 * Package and in every export the studio gets.
 *
 * Only proofed files with no pickups qualify; the rest are named with the
 * reason, so it's clear why a file was left alone. The server re-checks each
 * file on confirm, so one picked in the meantime doesn't get the flag.
 */

type Reason = "has_pickups" | "excluded" | "not_proofed" | "no_length" | "";

interface PlanFile {
  chapter_id: string;
  audio_filename: string;
  shown_filename?: string;   // the name it is known by (no "(v2)")
  eligible: boolean;
  reason: Reason;
  at: number | null;
}

const SKIPPED: Record<Exclude<Reason, "">, [string, string]> = {
  has_pickups: ["already has pickups", "already have pickups"],
  excluded: ["is excluded from proofing", "are excluded from proofing"],
  not_proofed: ["isn't proofed yet", "aren't proofed yet"],
  no_length: ["has no known length", "have no known length"],
};

export function NoIssuesDialog({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const base = `/api/modules/proofing/projects/${projectId}/pickups/no-issues`;
  const { data, isLoading, error } = useQuery({
    queryKey: ["no-issues", projectId],
    queryFn: () => api<{ files: PlanFile[] }>(base).then((r) => r.files),
    staleTime: 0,
  });

  const files = data ?? [];
  const eligible = files.filter((f) => f.eligible);
  const skipped = (Object.keys(SKIPPED) as Array<keyof typeof SKIPPED>)
    .map((reason) => ({ reason, n: files.filter((f) => f.reason === reason).length }))
    .filter((s) => s.n > 0);

  async function add() {
    setBusy(true);
    try {
      const r = await api<{ created: number }>(base, {
        method: "POST",
        body: JSON.stringify({ chapter_ids: eligible.map((f) => f.chapter_id) }),
      });
      qc.invalidateQueries({ queryKey: ["pickups", projectId] });
      toast(
        r.created
          ? `Added “No issues” to ${r.created} file${r.created === 1 ? "" : "s"}`
          : "Nothing added — those files have pickups now",
        r.created ? "success" : "info",
      );
      onClose();
    } catch (e: any) {
      toast(e.message || "Couldn't add the flags", "error");
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" {...backdropClose(() => { if (!busy) onClose(); })}>
      <div className="modal-card nid" role="dialog" aria-modal="true" aria-labelledby="nid-title"
        onClick={(e) => e.stopPropagation()}>
        <h3 id="nid-title">Mark clean files “No issues”</h3>
        <p className="nid-lede">
          Every proofed file with nothing picked up gets one flag, <b>“No issues”</b>, 3 seconds
          before its end. It goes into the Pickups Package and every export, so each file says
          what was found — including nothing.
        </p>

        {isLoading ? (
          <div className="nid-wait"><span className="spinner" style={{ width: 15, height: 15 }} /> Checking the files…</div>
        ) : error ? (
          <p className="dfd-warn">Couldn't check the files. Close this and try again.</p>
        ) : (
          <>
            {eligible.length > 0 ? (
              <div className="dfd-list nid-list">
                {eligible.map((f) => (
                  <div key={f.chapter_id} className="dfd-row">
                    <span className="dfd-name mono">{f.shown_filename || f.audio_filename}</span>
                    <span className="dfd-note">flag at {formatTime(f.at)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="nid-none">No file needs it — every proofed file already has pickups or a “No issues” flag.</p>
            )}
            {skipped.length > 0 && (
              <p className="nid-skipped">
                Left alone:{" "}
                {skipped.map((s, i) => (
                  <span key={s.reason}>
                    {i > 0 && " · "}
                    <b>{s.n}</b> {s.n === 1 ? SKIPPED[s.reason][0] : SKIPPED[s.reason][1]}
                  </span>
                ))}
              </p>
            )}
          </>
        )}

        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" disabled={busy || isLoading || eligible.length === 0} onClick={add}>
            {busy ? "Adding…" : eligible.length
              ? `Add to ${eligible.length} file${eligible.length === 1 ? "" : "s"}`
              : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}
